import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAccess } from "../contexts/AccessContext";
import {
  accessPathForLimitedRoute,
  firstVisibleLimitedPath,
} from "../limited-workspace/workspaceConfig";
import { useWorkflowTrial } from "../limited-workspace/useWorkflowTrial";
import { isSharedMeetlyPath } from "../utils/meetlyShare";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { loading, hasAccess, isPathVisible, firstVisiblePath } = useAccess();
  const location = useLocation();
  const workflowTrial = useWorkflowTrial();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const limitedWorkspace = user?.subscription?.workspace_mode === "limited";
  const onLimitedPath = location.pathname.startsWith("/limited-workspace");
  const onWorkflowTrialPath =
    location.pathname === "/screen-recording" ||
    location.pathname === "/activity-reports" ||
    location.pathname === "/activity-comparison";
  const limitedAccessPath = limitedWorkspace
    ? accessPathForLimitedRoute(location.pathname) ||
      (location.pathname === "/downloads" || onWorkflowTrialPath
        ? location.pathname
        : null)
    : null;
  const allowedLimitedUtilityPath =
    limitedWorkspace &&
    (location.pathname === "/profile" ||
      location.pathname === "/downloads" ||
      (workflowTrial.visible && onWorkflowTrialPath) ||
      location.pathname.startsWith("/settings/") ||
      location.pathname === "/todo/history" ||
      location.pathname === "/tasks/recently-deleted" ||
      /^\/tasks\/[^/]+$/.test(location.pathname));
  if (limitedWorkspace && !onLimitedPath && !allowedLimitedUtilityPath) {
    return <Navigate to="/limited-workspace/dashboard" replace />;
  }
  if (!limitedWorkspace && onLimitedPath) {
    return <Navigate to="/dashboard" replace />;
  }

  // Access-profile gating: a user with an assigned profile may only reach the
  // panels it enables. No profile → permissive (role-based visibility stands).
  // While permissions are still loading, render through to avoid a redirect flash.
  if (!loading && hasAccess) {
    const accessPath = limitedWorkspace ? limitedAccessPath : location.pathname;
    // A meeting shared with me is readable without Meetly; the API is the guard.
    if (accessPath && !isPathVisible(accessPath) && !isSharedMeetlyPath(location.pathname)) {
      return (
        <Navigate
          to={
            limitedWorkspace
              ? firstVisibleLimitedPath(isPathVisible)
              : firstVisiblePath()
          }
          replace
        />
      );
    }
  }

  return <>{children}</>;
}
